import type { VintedIntegration } from "src/interfaces/plaidIntegration.interface";
import { DirectImportSyncLockTtlMs } from "../syncLock";
import type { ResumeSyncStatus } from "../../types";
import type { SyncLockOptions } from "../syncLock";
import {
  discardAbandonedResume,
  getAbandonedResumeStatus,
  getActiveResumeLeaseExpiresAt,
  type AbandonedResumeInput,
} from "../resumeOffer";

const VintedDiscardFields = {
  salesResumeField: "vintedSalesImportResume",
  inventoryResumeField: "vintedInventoryImportResume",
  ttlMs: DirectImportSyncLockTtlMs,
} as const;

function toVintedResumeInput(
  integration: VintedIntegration,
  now = Date.now()
): AbandonedResumeInput {
  return {
    integrationId: integration.id,
    salesIsRunning: integration.salesIsRunning,
    inventoryIsRunning: integration.inventoryIsRunning,
    salesResume: integration.vintedSalesImportResume,
    inventoryResume: integration.vintedInventoryImportResume,
    ttlMs: DirectImportSyncLockTtlMs,
    platformLabel: "Vinted",
    now,
  };
}

export function getVintedAbandonedResumeStatus(
  integration: VintedIntegration,
  now = Date.now()
): ResumeSyncStatus | undefined {
  return getAbandonedResumeStatus(toVintedResumeInput(integration, now));
}

/**
 * When a Vinted checkpoint sits under a still-fresh lease, the wall-clock time
 * that lease expires (`*IsRunning + DirectImportSyncLockTtlMs`).
 */
export function getVintedActiveResumeLeaseExpiresAt(
  integration: VintedIntegration,
  now = Date.now()
): number | undefined {
  return getActiveResumeLeaseExpiresAt(toVintedResumeInput(integration, now));
}

/**
 * Dismissing the resume offer: drop the checkpoint plus the stale lease
 * (`*IsRunning` and its owner) so the offer is not re-derived from the
 * integration doc. No-op while a lease is still fresh (sync running elsewhere).
 */
export async function discardVintedResume(
  integrationId: string,
  job: ResumeSyncStatus["job"],
  options: Pick<SyncLockOptions, "getDb" | "now"> = {}
): Promise<boolean> {
  return await discardAbandonedResume(
    integrationId,
    job,
    VintedDiscardFields,
    options
  );
}
